// src/pages/supplier/WarehouseManagementPage.jsx
// Phase 5 — M5: Supplier warehouse & inventory management

import { useEffect, useState } from 'react'

const PINK  = '#FF3D7F'
const ORG   = '#FF8500'
const DARK  = '#1A1A2E'
const GREY  = '#9CA3AF'
const GR_LT = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

function StatCard({ label, value, sub, color = PINK }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontWeight: 600, color: DARK, marginTop: 2, fontSize: '0.9rem' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: GREY, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function StockBadge({ qty, reorder }) {
  const isLow  = reorder > 0 && qty <= reorder
  const isZero = qty === 0
  const bg     = isZero ? '#FEE2E2' : isLow ? '#FEF3C7' : '#DCFCE7'
  const color  = isZero ? '#991B1B' : isLow ? '#92400E' : '#166534'
  const label  = isZero ? 'Out of Stock' : isLow ? 'Low Stock' : 'In Stock'
  return (
    <span style={{ background: bg, color, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>
      {label}
    </span>
  )
}

export default function WarehouseManagementPage() {
  const [overview, setOverview] = useState(null)
  const [loading, setLoading]   = useState(true)
  const [tab, setTab]           = useState('warehouses')
  const [selected, setSelected] = useState(null)    // selected warehouse id
  const [showModal, setShowModal] = useState(false) // add warehouse modal
  const [form, setForm]         = useState({ name: '', address_line1: '', city: '', state: '', pincode: '', type: 'primary' })
  const [saving, setSaving]     = useState(false)
  const [stockModal, setStockModal] = useState(null) // {warehouseId}
  const [stockForm, setStockForm] = useState({ product_id: '', quantity: '', reorder_point: '', location_bin: '' })
  const [transferModal, setTransferModal] = useState(false)
  const [transferForm, setTransferForm]   = useState({ from_warehouse_id: '', to_warehouse_id: '', product_id: '', quantity: '' })

  const load = () => {
    setLoading(true)
    api('/supplier/warehouses/overview').then(r => {
      setOverview(r.data ?? r)
      setLoading(false)
    })
  }

  useEffect(() => { load() }, [])

  const createWarehouse = async () => {
    setSaving(true)
    await api('/supplier/warehouses', { method: 'POST', body: JSON.stringify(form) })
    setSaving(false); setShowModal(false); setForm({ name: '', address_line1: '', city: '', state: '', pincode: '', type: 'primary' })
    load()
  }

  const setDefault = async (id) => {
    await api(`/supplier/warehouses/${id}/default`, { method: 'POST' })
    load()
  }

  const upsertStock = async () => {
    setSaving(true)
    await api(`/supplier/warehouses/${stockModal}/stock`, { method: 'POST', body: JSON.stringify(stockForm) })
    setSaving(false); setStockModal(null); setStockForm({ product_id: '', quantity: '', reorder_point: '', location_bin: '' })
    load()
  }

  const doTransfer = async () => {
    setSaving(true)
    const r = await api('/supplier/warehouses/transfer', { method: 'POST', body: JSON.stringify(transferForm) })
    setSaving(false); setTransferModal(false)
    if (r.status !== 'success') alert(r.message); else load()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading warehouses…</div>

  const warehouses  = overview?.warehouses ?? []
  const lowStockCnt = overview?.low_stock_count ?? 0
  const totalProds  = overview?.total_products  ?? 0

  const selectedWh = selected ? warehouses.find(w => w.id === selected) : null
  const stockEntries = selectedWh?.stock_entries ?? []

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>Warehouse Management</h1>
          <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>Track inventory across all your storage locations</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setTransferModal(true)} style={{ padding: '10px 18px', borderRadius: 10, border: `1.5px solid ${PINK}`, background: '#fff', color: PINK, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
            Transfer Stock
          </button>
          <button onClick={() => setShowModal(true)} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
            + Add Warehouse
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard label="Warehouses"         value={warehouses.length}                color={PINK} />
        <StatCard label="Total Products"     value={totalProds}                        color={ORG}  />
        <StatCard label="Low Stock Alerts"   value={lowStockCnt} sub="Need restocking" color={lowStockCnt > 0 ? '#EF4444' : '#10B981'} />
        <StatCard label="Default Warehouse"  value={warehouses.find(w => w.is_default)?.name ?? '—'} color={DARK} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: GR_LT, borderRadius: 12, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {[['warehouses','All Warehouses'],['stock','Stock by Warehouse']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem', background: tab===k ? '#fff' : 'transparent', color: tab===k ? PINK : '#6B7280', boxShadow: tab===k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>{l}</button>
        ))}
      </div>

      {/* ── Warehouses List ── */}
      {tab === 'warehouses' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 16 }}>
          {warehouses.length === 0 && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 48, textAlign: 'center', color: GREY, gridColumn: '1/-1' }}>
              No warehouses yet. Add your first warehouse to get started.
            </div>
          )}
          {warehouses.map(wh => (
            <div key={wh.id} style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: wh.is_default ? `2px solid ${PINK}` : '2px solid transparent', position: 'relative' }}>
              {wh.is_default && <span style={{ position: 'absolute', top: 12, right: 12, background: PINK, color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700 }}>DEFAULT</span>}
              <div style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.1rem', color: DARK, marginBottom: 4 }}>{wh.name}</div>
              <div style={{ fontSize: '0.78rem', color: GREY, marginBottom: 12 }}>
                {wh.address_line1}, {wh.city}, {wh.state} — {wh.pincode}<br/>
                Code: <strong>{wh.code}</strong> &nbsp;|&nbsp; Type: {wh.type}
              </div>
              {wh.manager_name && <div style={{ fontSize: '0.8rem', color: '#4B5563', marginBottom: 12 }}>👤 {wh.manager_name} {wh.manager_phone ? `(${wh.manager_phone})` : ''}</div>}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => { setSelected(wh.id); setTab('stock') }} style={{ padding: '6px 14px', borderRadius: 8, border: `1.5px solid ${ORG}`, background: '#fff', color: ORG, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>View Stock</button>
                <button onClick={() => setStockModal(wh.id)} style={{ padding: '6px 14px', borderRadius: 8, border: `1.5px solid ${PINK}`, background: '#fff', color: PINK, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>Update Stock</button>
                {!wh.is_default && <button onClick={() => setDefault(wh.id)} style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px solid #D1D5DB', background: '#fff', color: '#6B7280', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>Set Default</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Stock by Warehouse ── */}
      {tab === 'stock' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {warehouses.map(wh => (
              <button key={wh.id} onClick={() => setSelected(wh.id)} style={{ padding: '7px 16px', borderRadius: 10, border: `1.5px solid ${selected===wh.id ? PINK : '#D1D5DB'}`, background: selected===wh.id ? PINK : '#fff', color: selected===wh.id ? '#fff' : DARK, fontWeight: 600, fontSize: '0.83rem', cursor: 'pointer' }}>
                {wh.name}
              </button>
            ))}
          </div>
          {!selectedWh ? (
            <div style={{ color: GREY, textAlign: 'center', padding: 40 }}>Select a warehouse above to view its stock.</div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, color: DARK }}>{selectedWh.name} — {stockEntries.length} products</div>
                <button onClick={() => setStockModal(selectedWh.id)} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: PINK, color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>+ Update Stock</button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: GR_LT }}>
                    {['Product','SKU','Available','Reserved','Reorder At','Bin','Status'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stockEntries.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: GREY }}>No stock entries yet.</td></tr>
                  )}
                  {stockEntries.map((s, i) => (
                    <tr key={s.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: DARK, fontSize: '0.87rem' }}>{s.product?.name ?? `Product #${s.product_id}`}</td>
                      <td style={{ padding: '12px 16px', color: GREY, fontSize: '0.82rem', fontFamily: 'monospace' }}>{s.product?.sku ?? '—'}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: DARK }}>{s.quantity_available?.toLocaleString()} m</td>
                      <td style={{ padding: '12px 16px', color: '#6B7280' }}>{s.quantity_reserved?.toLocaleString()} m</td>
                      <td style={{ padding: '12px 16px', color: '#6B7280' }}>{s.reorder_point > 0 ? `${s.reorder_point} m` : '—'}</td>
                      <td style={{ padding: '12px 16px', color: GREY, fontSize: '0.82rem' }}>{s.location_bin ?? '—'}</td>
                      <td style={{ padding: '12px 16px' }}><StockBadge qty={s.quantity_available} reorder={s.reorder_point} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Add Warehouse Modal ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Add Warehouse</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[['name','Warehouse Name','text',2],['address_line1','Address','text',2],['city','City','text',1],['state','State','text',1],['pincode','Pincode','text',1]].map(([k,l,t,cols]) => (
                <div key={k} style={{ gridColumn: `span ${cols}` }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>{l}</label>
                  <input type={t} value={form[k]} onChange={e => setForm(f => ({...f,[k]:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Type</label>
                <select value={form.type} onChange={e => setForm(f => ({...f,type:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }}>
                  {['primary','secondary','bonded','consignment'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={createWarehouse} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Create Warehouse'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Update Stock Modal ── */}
      {stockModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 400, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Update Stock</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[['product_id','Product ID','number'],['quantity','Quantity (metres)','number'],['reorder_point','Reorder Point','number'],['location_bin','Bin Location','text']].map(([k,l,t]) => (
                <div key={k}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>{l}</label>
                  <input type={t} value={stockForm[k]} onChange={e => setStockForm(f => ({...f,[k]:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setStockModal(null)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={upsertStock} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save Stock'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transfer Modal ── */}
      {transferModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Transfer Stock</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[['from_warehouse_id','From Warehouse ID'],['to_warehouse_id','To Warehouse ID'],['product_id','Product ID'],['quantity','Quantity (metres)']].map(([k,l]) => (
                <div key={k}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>{l}</label>
                  <input type="number" value={transferForm[k]} onChange={e => setTransferForm(f => ({...f,[k]:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setTransferModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={doTransfer} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: ORG, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Transferring…' : 'Transfer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
